import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useNavigate } from "react-router-dom";
import "../../styles/subjecttree.css";

const SubjectTree = ({ subjects, fieldTitle }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const navigate = useNavigate();

  const buildTree = (list, parentId = null) =>
    list
      .filter((item) => item.parent === parentId)
      .map((item) => ({
        ...item,
        children: buildTree(list, item.id),
      }));

  const toPersianNumber = (num) => {
    return new Intl.NumberFormat("fa-IR").format(num);
  };

  useEffect(() => {
    const data = {
      name: fieldTitle,
      children: buildTree(subjects),
    };

    const width = 1200;
    const height = 1000;
    const nodeWidth = 140;
    const nodeHeight = 40;
    const hSpacing = 200;
    const vSpacing = 80;
    const maxTextLength = 15;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Create elegant tooltip
    const tooltip = d3
      .select(containerRef.current)
      .append("div")
      .attr("class", "tree-tooltip")
      .style("position", "absolute")
      .style("visibility", "hidden")
      .style("opacity", 0)
      .style("transition", "opacity 0.3s, transform 0.2s");

    const zoomG = svg.append("g");
    svg
      .attr("width", "100%")
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);

    svg.call(
      d3.zoom().scaleExtent([0.4, 2]).on("zoom", (event) => {
        zoomG.attr("transform", event.transform);
      })
    );

    const root = d3.hierarchy(data);
    const treeLayout = d3.tree().nodeSize([hSpacing, vSpacing]);
    treeLayout(root);

    // Center the tree
    let minX = Infinity,
      maxX = -Infinity;
    root.descendants().forEach((d) => {
      if (d.x < minX) minX = d.x;
      if (d.x > maxX) maxX = d.x;
    });
    const xOffset = (width - (maxX - minX)) / 2 - minX;
    root.descendants().forEach((d) => (d.x += xOffset));

    zoomG
      .append("g")
      .selectAll("path")
      .data(root.links())
      .join("path")
      .attr("fill", "none")
      .attr("stroke", "#ccc")
      .attr("stroke-width", 1.5)
      .attr(
        "d",
        d3
          .linkVertical()
          .x((d) => d.x)
          .y((d) => d.y)
      );

    // Nodes
    const node = zoomG
      .append("g")
      .selectAll("g")
      .data(root.descendants())
      .join("g")
      .attr("transform", (d) => `translate(${d.x},${d.y})`)
      .style("cursor", (d) => (d.data.id ? "pointer" : "default"))
      .on("click", (event, d) => {
        if (d.data.id) navigate(`/subject/${d.data.id}`);
      });

    node
      .append("rect")
      .attr("width", nodeWidth)
      .attr("height", nodeHeight)
      .attr("x", -nodeWidth / 2)
      .attr("y", -nodeHeight / 2)
      .attr("rx", 10)
      .attr("fill", (d) =>
        d.depth === 0 ? "#01200f" : d.depth === 1 ? "#1e4f2a" : "#09814a"
      )
      .attr("stroke", "#333");

    // Store original names
    node.each(function (d) {
      d.fullName = d.data.name || d.data.title;
      d.displayText =
        d.fullName.length > maxTextLength
          ? d.fullName.substring(0, maxTextLength) + "..."
          : d.fullName;
    });

    node
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", "#fff")
      .style("font-size", "13px")
      .text((d) => d.displayText);

    // Tooltip for ALL nodes
    node
      .on("mouseover", function (event, d) {
        const [x, y] = d3.pointer(event, containerRef.current);

        tooltip
          .html(`
            <div class="tooltip-title">
              ${d.fullName}<br/>
              تعداد فناوری ها: ${toPersianNumber(d.data.report_count ?? 0)}
            </div>
          `)
          .style("left", `${x + 15}px`)
          .style("top", `${y + 15}px`)
          .style("visibility", "visible")
          .transition()
          .style("opacity", 1);
      })
      .on("mouseout", function () {
        tooltip
          .transition()
          .style("opacity", 0)
          .on("end", () => tooltip.style("visibility", "hidden"));
      });

    return () => {
      tooltip.remove();
    };
  }, [subjects, fieldTitle, navigate]);

  // ✅ JSX return is OUTSIDE useEffect
  return (
    <div ref={containerRef} className="subject-tree-container">
      <svg ref={svgRef}></svg>
    </div>
  );
};

export default SubjectTree;
